<template>
  <div class="partner-results" v-loader.opacity.tab="loading">
    <p class="attribution">{{ $t('partnerResults.disclaimer') }}</p>

    <div class="empty" v-if="!loading && !results.length">
      <BaseNoResults :title="$t('partnerResults.emptyTitle')" :text="$t('partnerResults.emptyText')"/>
    </div>

    <div class="result-card" v-for="r in results" :key="r.idempotencyKey">
      <div class="card-head">
        <div class="partner">
          <i class="fa fa-plug"></i>
          <span class="partner-name">{{ partnerLabel(r.partner) }}</span>
          <span class="analysis-tag">{{ r.analysis }}</span>
          <span class="eye-tag" v-if="r.laterality">{{ r.laterality }}</span>
        </div>
        <span class="badge" :class="statusClass(r.status)">{{ statusLabel(r.status) }}</span>
      </div>

      <div class="findings" v-if="r.status === 'completed' && r.findings">
        <span class="finding" v-for="f in summary(r)" :key="f.label">
          <span class="f-label">{{ f.label }}:</span> <span class="f-value">{{ f.value }}</span>
        </span>
      </div>

      <div class="error" v-else-if="r.status === 'error'">{{ r.error }}</div>

      <div class="card-actions" v-if="r.hasLaudo">
        <button class="btn btn-outline-secondary laudo-btn" type="button" :disabled="!!downloading" @click="openLaudo(r)" data-test="btnViewLaudo">
          <i class="fa" :class="downloading === r.idempotencyKey ? 'fa-spinner fa-spin' : 'fa-file-pdf-o'"></i>
          <span>{{ laudoButtonLabel(r) }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.partner-results {
  padding:                      10px 14px;
}

.attribution {
  font-size:                    12px;
  color:                        var(--Shades-60-W);
  border-left:                  3px solid var(--Complementary-Yellow, #DC9F3E);
  padding-left:                 10px;
  margin-bottom:                18px;
}

.result-card {
  border:                       1px solid var(--Shades-30-Border);
  border-radius:                8px;
  padding:                      14px 16px;
  margin-bottom:                12px;
}

.card-head {
  display:                      flex;
  justify-content:              space-between;
  align-items:                  center;
}

.partner {
  display:                      flex;
  align-items:                  center;
  gap:                          10px;

  .partner-name {
    font-weight:                600;
    color:                      var(--Brand-Primary);
  }

  .analysis-tag,
  .eye-tag {
    font-size:                  11px;
    font-weight:                600;
    letter-spacing:             0.5px;
    padding:                    2px 8px;
    border-radius:              10px;
    background:                 var(--Shades-10);
    color:                      var(--Shades-60-W);
  }
}

.findings {
  display:                      flex;
  flex-wrap:                    wrap;
  gap:                          16px;
  margin-top:                   12px;

  .finding {
    font-size:                  13px;
    .f-label { color: var(--Shades-60-W); }
    .f-value { color: var(--Brand-Primary); font-weight: 500; }
  }
}

.error {
  margin-top:                   10px;
  font-size:                    13px;
  color:                        var(--Support-Error);
}

.card-actions {
  margin-top:                   14px;

  .laudo-btn {
    gap:                        8px;
    padding:                    8px 16px;
    margin-right:               0;
    letter-spacing:             1.8px;

    i {
      color:                    var(--Brand-Secondary, #3CB391);
      font-size:                14px;
    }

    &:hover:not(:disabled) {
      border-color:             var(--Brand-Secondary, #3CB391);
    }

    &:disabled {
      opacity:                  0.5;
      cursor:                   not-allowed;
    }
  }
}
</style>

<script>
const LAUDO_MAX_WAIT_MS = 60000

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms))
}

export default {
  name: 'ExamPartnerResultsTab',
  props: ['exam_uuid', 'clinic_uuid'],
  data: () => ({
    loading: false,
    results: [],
    downloading: null,
    preparing: false,
    partnerLabels: { retinalyze: 'Retinalyze', mock: 'Mock' },
  }),
  mounted() {
    this.fetchResults()
  },
  methods: {
    fetchResults: async function() {
      if (!this.clinic_uuid || !this.exam_uuid) return
      this.loading = true
      NProgress.start()
      try {
        let response = await this.$connectorService.getExamResults({ clinicId: this.clinic_uuid, examId: this.exam_uuid })
        let data = response && response.result ? response.result : response
        this.results = data.results || []
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    partnerLabel(name) {
      return this.partnerLabels[name] || name
    },
    statusClass(status) {
      if (status === 'completed') return 'bg-success'
      if (status === 'error') return 'bg-danger'
      return 'bg-warning'
    },
    statusLabel(status) {
      return this.$i18n.t(`connector.jobStatus.${status}`)
    },
    summary(r) {
      const f = r.findings || {}
      const out = []
      const result = f[`answerType${r.analysis}`] !== undefined && f[`answerType${r.analysis}`] !== null
        ? f[`answerType${r.analysis}`]
        : f[`${r.analysis.toLowerCase()}Result`] !== undefined ? f[`${r.analysis.toLowerCase()}Result`] : f.drResult
      if (result !== undefined && result !== null) out.push({ label: this.$i18n.t('partnerResults.result'), value: result })
      const ann = f[`annotations${r.analysis}`]
      if (ann !== undefined && ann !== null) out.push({ label: this.$i18n.t('partnerResults.findingsCount'), value: ann })
      if (f.quality !== undefined && f.quality !== null) out.push({ label: this.$i18n.t('partnerResults.quality'), value: f.quality })
      return out
    },
    laudoButtonLabel(r) {
      if (this.downloading !== r.idempotencyKey) return this.$i18n.t('partnerResults.viewLaudo')
      return this.preparing
        ? this.$i18n.t('partnerResults.laudoPreparing')
        : this.$i18n.t('partnerResults.laudoLoading')
    },
    laudoErrorMessage(err) {
      const status = err && err.response ? err.response.status : null
      if (status === 404) return 'partnerResults.laudoNotAvailable'
      if (status === 502) return 'partnerResults.laudoFailed'
      if (status === 504 || !status) return 'partnerResults.laudoTimeout'
      return 'partnerResults.laudoFail'
    },
    openLaudo: async function(r) {
      if (this.downloading) return
      this.downloading = r.idempotencyKey
      this.preparing = false
      NProgress.start()
      try {
        const deadline = Date.now() + LAUDO_MAX_WAIT_MS
        let result = await this.$connectorService.getLaudo(r.idempotencyKey)

        while (result.status === 'preparing') {
          if (Date.now() >= deadline) {
            this.$message.popup(this, 'partnerResults.laudoTimeout', 'operationFail', 'error')
            NProgress.done(true)
            this.downloading = null
            this.preparing = false
            return
          }
          this.preparing = true
          await wait(result.retryAfter * 1000)
          result = await this.$connectorService.getLaudo(r.idempotencyKey)
        }

        const url = URL.createObjectURL(result.blob)
        window.open(url, '_blank')
        setTimeout(() => URL.revokeObjectURL(url), 60000)
      } catch (err) {
        console.log(err)
        this.$message.popup(this, this.laudoErrorMessage(err), 'operationFail', 'error')
      }
      NProgress.done(true)
      this.downloading = null
      this.preparing = false
    },
  },
}
</script>
